"use client";
import * as Yup from "yup";

import {
  createContext,
  Dispatch,
  ReactElement,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTheme } from "./Theme.context";
import { useParcels } from "./Parcels.context";
import { courierServiceForm } from "@/configs/courier/CourierSteps";

type TOpenCourierModalProps = {
  id: number;
};

interface ICourierContext {
  closeModal: () => void;
  courierSteps: {
    stepName: string;
    component: ReactElement<any, any>;
    validationSchema: Yup.ObjectSchema<any>;
  };
  courierStep: "delivery-info" | "delivery-payment";
  setCourierStep: Dispatch<
    SetStateAction<"delivery-info" | "delivery-payment">
  >;
  courierModalOpen: boolean;
  setCourierModalOpen: Dispatch<SetStateAction<boolean>>;
  openCourierModal: ({ id }: TOpenCourierModalProps) => void;
  deliveryRequestStatus: boolean;
  setDeliveryRequestStatus: Dispatch<SetStateAction<boolean>>;
}

const CourierContext = createContext<ICourierContext>(null);

export const useCourier = () => {
  return useContext(CourierContext);
};

export const CourierProvider = ({ children }) => {
  const theme = useTheme();
  const [courierModalOpen, setCourierModalOpen] = useState(false);
  const [courierStep, setCourierStep] = useState<
    "delivery-info" | "delivery-payment"
  >("delivery-info");
  const [deliveryRequestStatus, setDeliveryRequestStatus] = useState(false);
  const { modalOpen } = useParcels();
  useEffect(() => {
    if (typeof modalOpen === "boolean") {
      if (!modalOpen) {
        setDeliveryRequestStatus(false);
      }
    }
  }, [modalOpen]);

  const courierSteps = useMemo(() => {
    if (theme !== "") {
      return courierServiceForm[theme][courierStep];
    }
  }, [theme, courierStep]);

  const openCourierModal = ({ id }: TOpenCourierModalProps) => {
    setCourierModalOpen(true);
  };

  const resetStates = () => {
    setCourierStep("delivery-info");
    setCourierModalOpen(false);
  };

  const closeModal = () => {
    resetStates();
  };

  useEffect(() => {
    if (!courierModalOpen) {
      resetStates();
    }
  }, [courierModalOpen]);

  return (
    <CourierContext.Provider
      value={{
        closeModal,
        courierSteps,
        courierStep,
        setCourierStep,
        courierModalOpen,
        setCourierModalOpen,
        openCourierModal,
        deliveryRequestStatus,
        setDeliveryRequestStatus,
      }}
    >
      {children}
    </CourierContext.Provider>
  );
};
